import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AutosaveIndicator from "@/components/publisher/AutosaveIndicator";
import authService from "@/services/authService";
import InboxBell from "@/components/shared/InboxBell";
import ThemeToggle from "@/components/shared/ThemeToggle";
import UserMenu from "@/components/shared/UserMenu";
import { ArrowLeft, Search } from "lucide-react";

export default function NarratorHeader({ autosaveStatus, lastSaved }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);

  const isActive = (path) => location.pathname.startsWith(path);

  useEffect(() => {
    authService.getMe().then(setUser).catch(() => setUser(null));
  }, []);

  const displayName = user?.name || user?.email?.split("@")[0] || "User";

  return (
    <header className="sticky top-0 z-50 border-b border-[#eadfcd] bg-white/90 backdrop-blur dark:border-[#2f260f] dark:bg-[#070705]/95">
      <div className="mx-auto max-w-[1800px] px-5 py-4 md:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-8 lg:gap-12">
            <button
              className="flex items-center text-left"
              onClick={() => navigate("/dashboard")}
              data-testid="logo-link"
            >
              <img
                src="/images/golden-records-real-logo.png"
                alt="Golden Records"
                className="h-16 w-56 rounded-lg object-cover object-center"
              />
            </button>

            <nav className="hidden items-center gap-7 lg:flex">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
                data-testid="nav-dashboard"
              >
                Dashboard
              </button>
              <button
                onClick={() => navigate("/participate")}
                className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
                data-testid="nav-participate"
              >
                Participate
              </button>
              <button
                onClick={() => navigate("/publisher/workflow")}
                className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
                data-testid="nav-publisher"
              >
                Publisher
              </button>
              <button
                onClick={() => navigate("/narrator")}
                className={`text-sm font-medium ${
                  isActive("/narrator")
                    ? "border-b-2 border-[#c98905] pb-2 text-[#b77900]"
                    : "text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
                }`}
                data-testid="nav-narrator"
              >
                Narrator
              </button>
              <button
                onClick={() => navigate("/work")}
                className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
                data-testid="nav-work"
              >
                Work
              </button>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative hidden w-[360px] xl:block">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#c98905]" />
              <Input
                type="text"
                placeholder="Search books, projects, narrators..."
                className="h-11 rounded-lg border-[#eadfcd] bg-white pl-12 text-sm shadow-sm dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0] dark:placeholder:text-[#8f8677]"
              />
            </div>

            <ThemeToggle />

            {autosaveStatus && (
              <AutosaveIndicator status={autosaveStatus} lastSaved={lastSaved} />
            )}

            <InboxBell />

            <UserMenu user={user} displayName={displayName} />


            <Button
              onClick={() => navigate("/dashboard")}
              variant="outline"
              className="border-[#e0c89d] bg-white text-[#a46d00] hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
              data-testid="back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
